// ─── Campaign Status ────────────────────────────────────────────────────────

export enum CampaignStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  ARCHIVED = 'archived',
  CANCELLED = 'cancelled',
}

// ─── Package Types ──────────────────────────────────────────────────────────

export enum PackageType {
  STARTER = 'starter',
  STANDARD = 'standard',
  PROFESSIONAL = 'professional',
  PREMIUM = 'premium',
  ENTERPRISE = 'enterprise',
}

// ─── Package Config (fetched from backend API) ─────────────────────────────

export interface PackageConfig {
  /** Minimum number of analyses (inclusive) */
  min: number;
  /** Maximum number of analyses (inclusive). null = no limit */
  max: number | null;
  /** Fixed total price in EUR cents (e.g. 750000 = 7.500 €). 0 = individual */
  priceEurCents: number;
  /** Display label */
  label: string;
  /** Short marketing description */
  description: string;
}

/** Helper: format EUR cents to display string */
export function formatEurCents(cents: number): string {
  if (cents === 0) return 'Individuell';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/** Helper: format address range from PackageConfig */
export function formatAddressRange(cfg: PackageConfig): string {
  if (cfg.max === null) return `${cfg.min.toLocaleString('de-DE')}+ Adressen`;
  return `${cfg.min.toLocaleString('de-DE')} – ${cfg.max.toLocaleString('de-DE')} Adressen`;
}

// ─── Core Campaign Interface ────────────────────────────────────────────────

export interface Campaign {
  id: string;
  organizationId: string;
  createdBy: string;
  multiAnalysisId: string;
  title: string;
  description?: string;
  region?: string;
  packageType?: PackageType;
  targetAnalyses?: number;
  totalPrice?: number;
  isPaid?: boolean;
  paymentMethod?: string;
  status: CampaignStatus;
  completedAnalyses?: number;
  failedAnalyses?: number;
  processingAnalyses?: number;
  progress?: number; // Percentage 0-100
  settings?: {
    templateId?: string;
    [key: string]: any;
  };
  metadata?: Record<string, any>;
  tags?: string[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  purchasedAt?: Date;
  startedAt?: Date;
  completedAt?: Date;
  archivedAt?: Date;
  deletedAt?: Date;
  analysisCount?: number;
  campaignSiId?: string;
  creationSummary?: CampaignCreationSummary;
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface CreateCampaignDto {
  title: string;
  description?: string;
  organizationId: string;
  defaultAnnualConsumptionKwh: number;
  defaultElectricityPriceEurPerKwh: number;
  region?: string;
  packageType?: PackageType;
  targetAnalyses?: number;
  totalPrice?: number;
  tags?: string[];
  notes?: string;
  settings?: Record<string, any>;
  metadata?: Record<string, any>;
  /** Draft ID from validate-area (preferred) or resolve-postal-codes / validate-csv. */
  draftId: string;
}

export interface UpdateCampaignDto {
  title?: string;
  description?: string;
  region?: string;
  status?: CampaignStatus;
  tags?: string[];
  notes?: string;
  settings?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface MarkPaidDto {
  paymentMethod: string;
  paymentReference?: string;
}

// ─── CSV Upload ─────────────────────────────────────────────────────────────

export interface CsvValidationResult {
  validAddresses: number;
  /** Rows that were skipped due to hard errors. */
  invalidAddresses: number;
  duplicatesRemoved: number;
  /** Hard errors — rows that were skipped. */
  errors: CsvRowError[];
  /** Soft warnings — rows still included but missing optional data (e.g. MFH without meter count). */
  warnings: CsvRowError[];
  preview: AddressPreview[];
  /** Full list of valid addresses (for display only — backend stores them under draftId). */
  addresses?: AddressPreview[];
  /** Redis draft ID — pass to validate-area and campaign create instead of re-sending addresses. */
  draftId: string;
}

export interface CsvRowError {
  row: number;
  column?: string;
  reason: string;
}

export interface AddressPreview {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country?: string;
  annualConsumptionKwh?: number;
  electricityPriceCt?: number;
  isMultiFamilyHouse?: boolean;
  meterCount?: number;
}

export interface OutlierAddress {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  latitude: number;
  longitude: number;
}

export interface SiValidationResult {
  validCount: number;
  failedCount: number;
  canBeSaved: boolean;
  areaOk: boolean;
  areaSqm: number | null;
  failures: Array<{ index: number; statusCode: number; detail: string }>;
  requestFailures: Array<{ statusCode: number; detail: string }>;
  /** Draft ID containing only the addresses that passed validation. Use for campaign creation. */
  validatedDraftId: string;
}

export interface CampaignCreationSummary {
  analysesCreated: number;
  analysesFailed: number;
  /** Addresses that already existed in the SI DB and were reused */
  existingAddresses?: number;
  /** Addresses newly created in the SI DB */
  newAddresses?: number;
  failures: Array<{ index: number; reason: string }>;
}

// ─── Campaign Analyses Stats ─────────────────────────────────────────────────

export interface StageStats {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  queued: number;
  draft: number;
}

export interface TaskStats {
  done: number;
  active: number;
  failed: number;
  notStarted: number;
}

export interface CampaignAnalysesStats {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  queued: number;
  draft: number;
  byStage?: {
    idle: StageStats;
    geocut: StageStats;
    analyzing: StageStats;
    planning: StageStats;
    complete: StageStats;
  };
  byTask?: Record<number, TaskStats>;
}

// ─── Progress ───────────────────────────────────────────────────────────────

export interface CampaignProgress {
  campaignId: string;
  status: CampaignStatus;
  targetAnalyses: number;
  completedAnalyses: number;
  failedAnalyses: number;
  processingAnalyses: number;
  progress: number;
  estimatedCompletion?: string;
}

// ─── Campaign with Details ──────────────────────────────────────────────────

export interface CampaignWithDetails extends Campaign {
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  creator?: {
    id: string;
    name: string;
    email: string;
  };
  analyses?: any[];
}

// ─── Filters ────────────────────────────────────────────────────────────────

export interface CampaignFilters {
  status?: CampaignStatus;
  includeArchived?: boolean;
  search?: string;
}

// ─── Status Metadata (for UI display) ───────────────────────────────────────

export interface StatusMeta {
  label: string;
  color: string;
  icon: string;
  description: string;
}

export const CAMPAIGN_STATUS_METADATA: Record<CampaignStatus, StatusMeta> = {
  [CampaignStatus.DRAFT]: {
    label: 'Entwurf',
    color: 'bg-secondary text-muted-foreground',
    icon: 'lucideFilePen',
    description: 'Kampagne erstellt, noch nicht bezahlt',
  },
  [CampaignStatus.ACTIVE]: {
    label: 'Aktiv',
    color: 'bg-secondary text-foreground',
    icon: 'lucideCircleCheck',
    description: 'Bezahlt, bereit für CSV-Upload',
  },
  [CampaignStatus.PROCESSING]: {
    label: 'In Verarbeitung',
    color: 'bg-accent text-accent-foreground',
    icon: 'lucideLoader',
    description: 'Analysen werden verarbeitet',
  },
  [CampaignStatus.COMPLETED]: {
    label: 'Abgeschlossen',
    color: 'bg-success-soft text-success',
    icon: 'lucideCircleCheckBig',
    description: 'Alle Analysen fertig',
  },
  [CampaignStatus.FAILED]: {
    label: 'Fehlgeschlagen',
    color: 'bg-danger-soft text-danger',
    icon: 'lucideTriangleAlert',
    description: 'Kritischer Fehler aufgetreten',
  },
  [CampaignStatus.ARCHIVED]: {
    label: 'Archiviert',
    color: 'bg-secondary text-muted-foreground',
    icon: 'lucideArchive',
    description: 'Kampagne archiviert (nur Lesen)',
  },
  [CampaignStatus.CANCELLED]: {
    label: 'Storniert',
    color: 'bg-muted text-muted-foreground',
    icon: 'lucideCircleX',
    description: 'Kampagne storniert',
  },
};

// ─── Package Type Metadata (static fallback — prefer dynamic API data) ──────

export interface PackageMeta {
  label: string;
  description: string;
  addressRange: string;
  priceRange: string;
}

/**
 * Static fallback metadata — only used if the backend API is unavailable.
 * Dynamic data from GET /campaigns/package-config should always be preferred.
 */
export const PACKAGE_TYPE_METADATA: Record<PackageType, PackageMeta> = {
  [PackageType.STARTER]: {
    label: 'Starter',
    description: 'Ideal für kleinere Gebiete und Einstiegsprojekte',
    addressRange: '500 – 1.000 Adressen',
    priceRange: '7.500 €',
  },
  [PackageType.STANDARD]: {
    label: 'Standard',
    description: 'Mittelgroße Kampagnen für kommunale Quartiere',
    addressRange: '1.001 – 3.000 Adressen',
    priceRange: '15.000 €',
  },
  [PackageType.PROFESSIONAL]: {
    label: 'Professional',
    description: 'Große Gebietsabdeckung für Städte und Landkreise',
    addressRange: '3.001 – 8.000 Adressen',
    priceRange: '25.000 €',
  },
  [PackageType.PREMIUM]: {
    label: 'Premium',
    description: 'Flächendeckende Kampagnen für Regionen',
    addressRange: '8.001 – 15.000 Adressen',
    priceRange: '30.000 €',
  },
  [PackageType.ENTERPRISE]: {
    label: 'Enterprise',
    description: 'Individuelle Lösung — Preis auf Anfrage',
    addressRange: '15.001+ Adressen',
    priceRange: 'Individuell',
  },
};
